import React, { useState, useEffect, useRef } from 'react';
import AudioPlayer from 'react-audio-player';
import { getSectionPartAudio, getSectionPartImagesIndex } from '../../services/apiService';
import { useParams } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import './Audio.css';

export default function Audio() {
  const { sectionPartId } = useParams();
  const { t } = useTranslation(['book', 'dashboard']);

  const [sesDosyasiYolu, setSesDosyasiYolu] = useState('');
  const [currentSpreadIndex, setCurrentSpreadIndex] = useState(0); // açık kitap yayılımı: 0 = sayfa 1-2, 1 = sayfa 3-4, ...
  const [zoomedSide, setZoomedSide] = useState(null); // 'left' | 'right' | null
  const [zoomOrigin, setZoomOrigin] = useState({ x: 0.5, y: 0.5 });
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [images, setImages] = useState([]);
  const volume = useRef(0.5);

  const ZOOM_SCALE = 2.0;
  const PAN_SENSITIVITY = 0.35;

  const spreadCount = Math.ceil(images.length / 2);
  const leftImageIndex = currentSpreadIndex * 2;
  const rightImageIndex = currentSpreadIndex * 2 + 1;
  const hasLeftImage = leftImageIndex < images.length;
  const hasRightImage = rightImageIndex < images.length;

  useEffect(() => {
    document.title = t('headers:audio');
    fetchAudio();
    fetchImages(0);
  }, [sectionPartId]);

  const fetchAudio = async () => {
    const url = await getSectionPartAudio(sectionPartId);
    setSesDosyasiYolu(url);
  };

  const fetchImages = async (index) => {
    while (true) {
      const response = await getSectionPartImagesIndex(sectionPartId, index);
      if (!response.image) {
        break;
      }
      setImages((prevImages) => [...prevImages, response.image]);
      if (response.length <= index + 1) {
        break;
      }
      index++;
    }
  };

  const handleZoom = (side, e) => {
    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setZoomOrigin({ x, y });
    setPanOffset({ x: 0, y: 0 });
    setZoomedSide((prev) => (prev === side ? null : side));
  };

  const handlePanMove = (side, e) => {
    if (zoomedSide !== side) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const x = (centerX - e.clientX) * PAN_SENSITIVITY;
    const y = (centerY - e.clientY) * PAN_SENSITIVITY;
    setPanOffset({ x, y });
  };

  const handleSpreadChange = (direction) => {
    setZoomedSide(null);
    if (direction === 'next') {
      setCurrentSpreadIndex((prev) => (prev >= spreadCount - 1 ? 0 : prev + 1));
    } else {
      setCurrentSpreadIndex((prev) => (prev === 0 ? spreadCount - 1 : prev - 1));
    }
  };

  const pageIndicatorText = hasRightImage
    ? `${leftImageIndex + 1}-${rightImageIndex + 1} / ${images.length}`
    : `${leftImageIndex + 1} / ${images.length}`;

  const preventDownload = (e) => {
    e.preventDefault();
  };

  return (
    <div className="audio-page">
      <Container className="px-3 px-md-4">
        {images.length >= 1 ? (
          <>
            <div className="audio-image-card">
              <div className="audio-book">
                <div className="audio-book-pages">
                  {/* Sol sayfa — tıkla zoom, mouse hareketiyle gez */}
                  <div
                    className={`audio-book-page audio-book-page-left ${zoomedSide === 'left' ? 'zoomed' : ''}`}
                    onClick={(e) => hasLeftImage && handleZoom('left', e)}
                    onMouseMove={(e) => zoomedSide === 'left' && handlePanMove('left', e)}
                    title={hasLeftImage ? t('book:click-zoom', 'Tıklayarak yakınlaştır') : undefined}
                  >
                    {hasLeftImage ? (
                      <img
                        src={images[leftImageIndex]}
                        alt={`Sayfa ${leftImageIndex + 1}`}
                        className={zoomedSide === 'left' ? 'zoomed' : ''}
                        style={{
                          transformOrigin: `${zoomOrigin.x * 100}% ${zoomOrigin.y * 100}%`,
                          ...(zoomedSide === 'left' && { transform: `scale(${ZOOM_SCALE}) translate(${panOffset.x}px, ${panOffset.y}px)` }),
                        }}
                        onContextMenu={preventDownload}
                        onDragStart={preventDownload}
                        draggable="false"
                      />
                    ) : (
                      <div className="audio-book-page-blank" aria-hidden />
                    )}
                  </div>

                  {/* Cilt */}
                  <div className="audio-book-spine" aria-hidden />

                  {/* Sağ sayfa — tıkla zoom, mouse hareketiyle gez */}
                  <div
                    className={`audio-book-page audio-book-page-right ${zoomedSide === 'right' ? 'zoomed' : ''}`}
                    onClick={(e) => hasRightImage && handleZoom('right', e)}
                    onMouseMove={(e) => zoomedSide === 'right' && handlePanMove('right', e)}
                    title={hasRightImage ? t('book:click-zoom', 'Tıklayarak yakınlaştır') : undefined}
                  >
                    {hasRightImage ? (
                      <img
                        src={images[rightImageIndex]}
                        alt={`Sayfa ${rightImageIndex + 1}`}
                        className={zoomedSide === 'right' ? 'zoomed' : ''}
                        style={{
                          transformOrigin: `${zoomOrigin.x * 100}% ${zoomOrigin.y * 100}%`,
                          ...(zoomedSide === 'right' && { transform: `scale(${ZOOM_SCALE}) translate(${panOffset.x}px, ${panOffset.y}px)` }),
                        }}
                        onContextMenu={preventDownload}
                        onDragStart={preventDownload}
                        draggable="false"
                      />
                    ) : (
                      <div className="audio-book-page-blank" aria-hidden />
                    )}
                  </div>
                </div>
              </div>

              {images.length > 1 && (
                <div className="audio-nav">
                  <button
                    type="button"
                    className="audio-nav-btn"
                    disabled={currentSpreadIndex === 0}
                    onClick={() => handleSpreadChange('prev')}
                  >
                    {t('book:prev')}
                  </button>
                  <span className="audio-page-indicator">
                    {pageIndicatorText}
                  </span>
                  <button
                    type="button"
                    className="audio-nav-btn"
                    onClick={() => handleSpreadChange('next')}
                  >
                    {currentSpreadIndex >= spreadCount - 1 ? t('book:toFirst') : t('book:next')}
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="audio-loading">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading</span>
            </div>
            <p className="audio-loading-text">{t('dashboard:loading')}</p>
          </div>
        )}

        <div className="audio-player-card">
          <div className="audio-player-wrapper">
            <AudioPlayer
              src={sesDosyasiYolu}
              autoPlay
              controls
              controlsList="nodownload"
              volume={volume.current}
              onVolumeChanged={(e) => (volume.current = e.target.volume)}
            />
          </div>
        </div>
      </Container>
    </div>
  );
}
